// @ts-check
import axios from 'axios';
import * as z from 'zod';

import Bugsnag, { tre } from '@/plugins/bugsnag';

axios.defaults.baseURL = import.meta.env.VITE_APP_API_URL;

/**
 * 容錯解析函式
 * @template T - Schema 型別
 * @param schema - Zod schema
 * @param payload - 要解析的資料
 * @returns 解析後的資料，如果失敗則回傳原始資料
 */
export const fallbackParse = <T extends z.ZodType>(
  schema: T,
  payload: unknown,
): z.infer<T> => {
  const result = schema.safeParse(payload);
  if (!result.success) {
    Bugsnag.leaveBreadcrumb(
      'fallbackParse',
      { data: payload },
      'log',
    );

    tre(result.error);
    // 當解析失敗時，回傳原始資料並進行類型斷言
    // 這是一個容錯機制，在生產環境中避免應用程式崩潰

    return payload as z.infer<T>;
  }

  return result.data;
};

export const handsupPostConfigSchema = z.object({
  id: z.number(),
  title: z.string(),
  share_url: z.string().optional().nullable(),
});

export const handsupStoreConfigSchema = z.object({
  slug: z.string(),
  name: z.string(),
  locale: z.string(),
  region: z.string(),
  currency: z.string(),
  cms_handsup_device: z.boolean(),
  avatar_url: z.string().optional().nullable(),
});

export const handsupConfigSchema = z.object({
  post: handsupPostConfigSchema.nullish(),
  store: handsupStoreConfigSchema,
});

export const cloudImageSchema = z.object({
  id: z.number(),
  url: z.string(),
});

export const productVariantSchema = z.object({
  id: z.number(),
  name: z.string(),
  price: z.string(),
  original_price: z.string().nullable(),
  salable_quantity: z.number(),
  images: z.array(cloudImageSchema),
});

export const productSchema = z.object({
  id: z.number(),
  name: z.string(),
  skus: z.array(productVariantSchema),
  cover_image_id: z.number().nullable(),
  images: z.array(cloudImageSchema),
  salable_status: z.enum(['before_sell', 'end_sell', 'init', 'on_sell', 'sold_out']),
});

export const streamPostImageSchema = z.object({
  id: z.number(),
  url: z.string(),
});

export const streamPostPublicationSchema = z.object({
  post_id: z.number(),
  live_status: z.enum(['live_stopped', 'live', 'unpublished']),
  started_at: z.string().optional().nullable(),
  channel: z.object({
    id: z.number(),
    name: z.string(),
    type: z.string(),
  }),
});

export const archiveChapterDataLabelSchema = z.object({
  name: z.string(),
  started_time: z.number(),
});

export const archiveChapterDataSchema = z.object({
  started_time: z.number(),
  labels: z.array(archiveChapterDataLabelSchema),
});

export const archiveClipsDataSchema = z.object({
  id: z.number(),
  title: z.string(),
  started_time: z.number(),
});

export const archiveRawSchema = z.object({
  type: z.literal('raw'),
  data: z.null(),
});

export const archiveChapterSchema = z.object({
  type: z.literal('chapters'),
  data: archiveChapterDataSchema,
});

export const archiveClipsSchema = z.object({
  type: z.literal('clips'),
  data: z.array(archiveClipsDataSchema),
});

export const streamPostSchema = z.object({
  id: z.number(),
  title: z.string(),
  upcoming_title: z.string().nullable(),
  replay_expired_at: z.string().optional().nullable(),
  upcoming_at: z.string().optional().nullable(),
  publications: z.array(streamPostPublicationSchema),
  image: z.array(streamPostImageSchema),
  share_url: z.string().optional().nullable(),
  stream_info: z.object({
    replay_url: z.string().optional().nullable(),
    replay_url_mp4: z.string().optional().nullable(),
  }),
  archive: z.union([
    archiveChapterSchema,
    archiveRawSchema,
    archiveClipsSchema,
  ]).optional().nullable(),
  is_handsup_caption_ready: z.boolean().optional(),
});

export const upcomingLiveSchema = streamPostSchema;

export const liveHistoryPublicationSchema = streamPostPublicationSchema.extend({
  started_at: z.string(),
});

export const liveHistorySchema = streamPostSchema.extend({
  publications: z.array(liveHistoryPublicationSchema),
});

export const templateDataResponseSchema = z.object({
  handsup_config: handsupConfigSchema,
  layout_content: z.string(),
  product_list: z.array(productSchema),
  upcoming_post_list: z.array(upcomingLiveSchema),
  live_history_list: z.array(liveHistorySchema),
  current_stream_status: streamPostSchema.optional().nullable(),
});

export const scheduledPostSchema = z.object({
  post_id: z.number(),
  title: z.string(),
  scheduled_started_at: z.string(),
});

export const realtimeServiceProviderSchema = z.object({
  type: z.enum(['ably', 'pubnub']),
  /** `notification|sdk_token|${SDK_TOKEN}`*/
  channel_list: z.array(z.string()),
  subscribe_key: z.string(),
});

export const generalTemplateHandsupStoreConfigSchema = handsupStoreConfigSchema.extend({
  realtime_service_provider: realtimeServiceProviderSchema,
});

export const generalTemplateHandsupConfigSchema = z.object({
  post: handsupPostConfigSchema.nullish(),
  store: generalTemplateHandsupStoreConfigSchema,
});

export const generalTemplateDataResponseSchema = templateDataResponseSchema.extend({
  handsup_config: generalTemplateHandsupConfigSchema,
  scheduled_post_list: z.array(scheduledPostSchema),
  current_stream_status: streamPostSchema.optional().nullable(),
});

export type HandsupConfig = z.infer<typeof handsupConfigSchema>;
export type GeneralTemplateHandsupConfig = z.infer<typeof generalTemplateHandsupConfigSchema>;
export type Product = z.infer<typeof productSchema>;
export type RealtimeServiceProvider = z.infer<typeof realtimeServiceProviderSchema>;
export type TemplateData = z.infer<typeof templateDataResponseSchema>;
export type GeneralTemplateData = z.infer<typeof generalTemplateDataResponseSchema>;

export const getTemplateData = async (sdkToken: string, templateId: string) => {
  try {
    const response = await axios.get(`/api/v1/stores/${sdkToken}/cms-sdk-setting/templates/${templateId}`);

    return Object.freeze({
      ok: true,
      data: fallbackParse(templateDataResponseSchema, response.data),
    });
  } catch (error) {
    tre(error);
    return Object.freeze({
      ok: false,
    });
  }
};

export const getGeneralTemplateData = async (sdkToken: string) => {
  try {
    const response = await axios.get(`/api/v1/stores/${sdkToken}/general-cms-sdk-setting/template`);

    return Object.freeze({
      ok: true,
      data: fallbackParse(generalTemplateDataResponseSchema, response.data),
    });
  } catch (error) {
    tre(error);
    return Object.freeze({
      ok: false,
    });
  }
};

// #region Fast Checkout
const postFastCheckoutResponseSchema = z.object({
  redirect_url: z.url(),
});

export const postFastCheckout = async (postId: number, sku: number) => {
  try {
    const response = await axios.post(`/api/v1/enterprise/posts/${postId}/fast-checkout`, {
      skus: [{
        id: sku,
        quantity: 1,
      }],
    });

    return Object.freeze({
      ok: true,
      data: fallbackParse(postFastCheckoutResponseSchema, response.data),
    });
  } catch (error) {
    tre(error);
    return Object.freeze({
      ok: false,
    });
  }
};
// #endregion Fast Checkout

// #region Maintenance Status
const getMaintenanceStatusResponseSchema = z.object({
  maintaining: z.boolean(),
});

export const getMaintenanceStatus = async () => {
  try {
    const response = await axios.get(import.meta.env.VITE_APP_MAINTENANCE_STATUS_URL);
    return Object.freeze({
      ok: true,
      data: fallbackParse(getMaintenanceStatusResponseSchema, response.data),
    });
  } catch (error) {
    tre(error);
    return Object.freeze({
      ok: false,
    });
  }
};
// #endregion Maintenance Status

// #region Record Product Clicked Event
/**
 * 記錄產品點擊事件
 * @param params - 事件參數物件
 * @param params.postId - 貼文 ID（可選）
 * @param params.productId - 產品 ID
 * @param params.skuId - SKU ID（可選）
 * @param params.storeSlug - 商店 slug
 * @param params.pnActualId - PubNub 實際 ID（可選）
 * @param params.source - 來源類型("cms" | "market" | "stream_room")
 * @param params.entryPoint - 進入點（可選，目前僅支援 "promoting-products"）
 * @example
 * await recordProductClicked({
 *   postId: 123,
 *   productId: 456,
 *   skuId: 789,
 *   storeSlug: 'my-store',
 *   pnActualId: 'pn-xxx',
 *   source: 'cms',
 *   entryPoint: 'promoting-products'
 * })
 */
export const recordProductClicked = ({ postId, productId, skuId, storeSlug, pnActualId, source, entryPoint }: { postId: number; productId: number; skuId: number; storeSlug: string; pnActualId: string; source: string; entryPoint?: 'promoting-products' }) => {
  const url = '/user-behaviors/analytics/product-clicked-event';
  const payload = {
    post_id: postId,
    product_id: productId,
    sku_id: skuId,
    store_slug: storeSlug,
    pn_actual_id: pnActualId,
    source,
    ...(entryPoint && { entry_point: entryPoint }),
  };

  return fetch(
    `${import.meta.env.VITE_APP_SERVICE_API_URL}${url}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    },
  ).catch(() => { /* ignore */ });
};
// #endregion Record Product Clicked Event
